import dayjs from 'dayjs';
import { useInfiniteQuery } from 'react-query';
import { AadeBookInvoiceType } from '../../../interfaces/myData.interface';
import { apiRQ } from '../../api';

export interface ReceivedDocsFilters {
  dateFrom: string; // YYYY-MM-DD (converted to DD/MM/YYYY for API)
  dateTo: string; // YYYY-MM-DD
  invType: string;
  counterVatNumber: string;
  markSearch: string;
}

interface ReceivedDocsPage {
  docs: AadeBookInvoiceType[];
  continuationToken: { nextPartitionKey: string; nextRowKey: string } | null;
}

const fetchReceivedDocs = async (
  filters: ReceivedDocsFilters,
  pageParam?: { nextPartitionKey: string; nextRowKey: string },
): Promise<ReceivedDocsPage> => {
  const params: Record<string, string | number> = {};

  if (filters.markSearch) {
    const markNum = Number(filters.markSearch);
    params.mark = markNum - 1;
    params.maxMark = markNum;
  } else {
    params.mark = 0;
    if (filters.dateFrom) params.dateFrom = dayjs(filters.dateFrom).format('DD/MM/YYYY');
    if (filters.dateTo) params.dateTo = dayjs(filters.dateTo).format('DD/MM/YYYY');
    if (filters.invType) params.invType = filters.invType;
    if (filters.counterVatNumber) params.counterVatNumber = filters.counterVatNumber;
  }

  if (pageParam?.nextPartitionKey) params.nextPartitionKey = pageParam.nextPartitionKey;
  if (pageParam?.nextRowKey) params.nextRowKey = pageParam.nextRowKey;

  const resp = await apiRQ.get('/gsis/recipient-docs', { params });
  const requestedDoc = resp.data?.data?.RequestedDoc;
  const invoice = requestedDoc?.invoicesDoc?.invoice;
  let docs: AadeBookInvoiceType[] = invoice ? (Array.isArray(invoice) ? invoice : [invoice]) : [];
  if (filters.counterVatNumber) {
    docs = docs.filter((d) => String(d.issuer?.vatNumber) === filters.counterVatNumber.trim());
  }
  const continuationToken = requestedDoc?.continuationToken ?? null;

  return { docs, continuationToken };
};

export const useReceivedDocs = (filters: ReceivedDocsFilters) => {
  return useInfiniteQuery<ReceivedDocsPage, Error>(
    ['receivedDocs', JSON.stringify(filters)],
    ({ pageParam }) => fetchReceivedDocs(filters, pageParam),
    {
      getNextPageParam: (lastPage) => lastPage.continuationToken ?? undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
      staleTime: 30_000,
    },
  );
};
